import { RefreshCcw } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Image } from '@unistocks/components/basic/image';
import { Form } from '@unistocks/components/form/form';
import { requiredSchema } from '@unistocks/components/form/validators';
import { Loader } from '@unistocks/components/loading/spinner/loader';
import { Page } from '@unistocks/components/page/page';
import { toast } from '@unistocks/components/toast';
import { AddAccountHeaderRight } from '@unistocks/features/external-accounts/header/header-right';
import { AuthenticateExternalAccountModal } from '@unistocks/features/external-accounts/modals/authenticate-external-account';
import { t } from '@unistocks/localization';
import {
  AccountProviderDto,
  AuthenticationMethod,
  BankIdStatus
} from '@unistocks/proxy/axios-client';
import {
  externalAccountsAuthenticateCollect,
  externalAccountsAuthenticateInit
} from '@unistocks/proxy/axios-client/ExternalAccountsClient';
import {
  externalAccountsGetAccountsQueryKey,
  useExternalAccountsGetProvidersQuery
} from '@unistocks/proxy/axios-client/ExternalAccountsQuery';
import { SelectOption } from '@unistocks/types';
import { createURL } from 'expo-linking';
import { useRouter } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { useCallback, useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { Platform } from 'react-native';
import { z } from 'zod';

interface AddAccountFormValues {
  countryCode: string;
  provider: string;
}

const schemaFactory = () =>
  z.object({
    countryCode: requiredSchema(z.string(), t('::Import:BankAccount:Market')),
    provider: requiredSchema(
      z.string(),
      undefined,
      t('::ValidationError:Required:Selection', { 0: t('::Import:Bank') })
    )
  });

async function handleRedirectAuth(
  provider: AccountProviderDto,
  queryClient: ReturnType<typeof useQueryClient>,
  router: ReturnType<typeof useRouter>,
): Promise<boolean> {
  try {
    const callbackUri = createURL('external-accounts/add-account');

    const { redirectUri: authUrl } = await externalAccountsAuthenticateInit(provider.name, {
      providerIdentifier: provider.providerIdentifier,
      userIdentifier: null,
      autostartToken: false,
      redirectUri: callbackUri
    });

    if (!authUrl) {
      return false;
    }

    const result = await WebBrowser.openAuthSessionAsync(authUrl);

    if (result.type !== 'success' || !result.url) {
      return false;
    }

    const callbackUrl = new URL(result.url);
    const code = callbackUrl.searchParams.get('code');
    if (!code) {
      return false;
    }

    const { status } = await externalAccountsAuthenticateCollect(provider.name, {
      provider: {
        name: provider.name,
        providerIdentifier: provider.providerIdentifier,
        market: provider.market
      },
      code
    });

    if (status !== BankIdStatus.Complete) {
      return false;
    }

    queryClient.invalidateQueries({ queryKey: externalAccountsGetAccountsQueryKey() });
    router.replace({
      pathname: '/(tabs)/(menu)/external-accounts/import',
      params: {
        provider: provider.name,
        identifier: provider.providerIdentifier || '',
        accountIdentifier: provider.accountIdentifier || ''
      }
    });

    return true;
  } catch {
    return false;
  }
}

export function AddAccount() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const schema = schemaFactory();
  const [formValue, setFormValue] = useState<Partial<AddAccountFormValues>>({
    countryCode: 'SE'
  });
  const [bankIdModalOpen, setBankIdModalOpen] = useState(false);

  useEffect(() => {
    if (Platform.OS === 'android') {
      WebBrowser.warmUpAsync();

      return () => {
        WebBrowser.coolDownAsync();
      };
    }
  }, []);

  const { data, isPending, isSuccess } = useExternalAccountsGetProvidersQuery(
    {
      countryCode: formValue.countryCode!
    },
    {
      enabled: !!formValue.countryCode,
      structuralSharing: false,
      select: ({ items }): [AccountProviderDto[], SelectOption[]] => [
        items,
        items.map<SelectOption>(provider => ({
          key: provider.displayName!,
          value: provider.name,
          icon: <Image rounded size={'$2.5'} source={provider.imageUri} />
        }))
      ]
    }
  );

  const onSubmit = async (values: AddAccountFormValues) => {
    setFormValue(values);

    const provider = data?.[0].find(provider => provider.name === values.provider)!;

    switch (provider?.authenticationMethod) {
      case AuthenticationMethod.BankID:
        setBankIdModalOpen(true);
        break;

      case AuthenticationMethod.Redirect: {
        const success = await handleRedirectAuth(provider, queryClient, router);
        if (!success) {
          toast.error(t('::ExternalAccounts:Create:Error', { 0: provider.displayName }));
        }
        break;
      }
    }
  };

  const onAuthenticateSuccess = useCallback(() => {
    if (formValue.provider) {
      setBankIdModalOpen(false);
      queryClient.invalidateQueries({ queryKey: externalAccountsGetAccountsQueryKey() });

      const selectedProvider = data?.[0].find(provider => provider.name === formValue.provider);
      router.replace({
        pathname: '/(tabs)/(menu)/external-accounts/import',
        params: {
          provider: formValue.provider,
          identifier: selectedProvider?.providerIdentifier || '',
          accountIdentifier: selectedProvider?.accountIdentifier || ''
        }
      });
    }
  }, [data, formValue.provider, queryClient, router]);

  const onAuthenticateError = useCallback(
    (_e: any) => {
      const selectedProvider = data?.[0].find(provider => provider.name === formValue.provider);
      toast.error(t('::ExternalAccounts:Create:Error', { 0: selectedProvider?.displayName }));
    },
    [data, formValue.provider]
  );

  const renderContent = () => {
    if (isPending) {
      return <Loader>{t('::Import:BankAccount:LoadingAccounts')}</Loader>;
    }

    if (isSuccess) {
      const [providers, selectOptions] = data;

      return (
        <Page.Content>
          <Form
            schema={schema}
            initialValues={{
              countryCode: 'SE',
              provider: providers[0]?.name
            }}
            onSubmit={onSubmit}
            triggerText={t('::Continue')}
          >
            <AddAccountFormContent providers={providers} selectOptions={selectOptions} />
          </Form>
        </Page.Content>
      );
    }

    return null;
  };

  if (isSuccess) {
    const [providers] = data;
    const selectedProvider = providers.find(provider => provider.name === formValue.provider);

    return (
      <Page size={'small'}>
        <Page.ScrollView
          headerLogo={false}
          headerTitle={t('::ExternalAccounts:AddAccount')}
          headerIcon={<AddAccountHeaderRight />}
          breadcrumbs={[
            {
              key: 'external-accounts',
              rootPathName: 'external-accounts',
              name: t('::ExternalAccounts'),
              href: '/(tabs)/(menu)/external-accounts',
              Icon: props => <RefreshCcw {...props} />
            }
          ]}
        >
          {renderContent()}
        </Page.ScrollView>

        {selectedProvider ? (
          <AuthenticateExternalAccountModal
            open={bankIdModalOpen}
            onOpenChange={setBankIdModalOpen}
            provider={selectedProvider}
            onAuthenticateSuccess={onAuthenticateSuccess}
            onAuthenticateError={onAuthenticateError}
            headerSubtitle={t('::Import:Authenticate', { 0: selectedProvider.displayName })}
          />
        ) : null}
      </Page>
    );
  }
}

type AddAccountFormContentProps = {
  providers: AccountProviderDto[];
  selectOptions: SelectOption[];
};

const AddAccountFormContent: React.FC<AddAccountFormContentProps> = ({
  providers,
  selectOptions
}) => {
  const provider = useWatch<AddAccountFormValues, 'provider'>({ name: 'provider' });
  const selectedProvider = providers.find(p => p.name === provider);

  return (
    <Form.Select
      name={'provider'}
      label={t('::Import:Bank')}
      placeholder={t('::SelectWhat', { 0: t('::Import:Bank').toLocaleLowerCase() })}
      isRequired
      options={selectOptions}
      triggerProps={{
        gap: 0,
        icon: selectedProvider?.imageUri ? (
          <Image rounded size={'$2.5'} source={selectedProvider.imageUri} />
        ) : undefined
      }}
    />
  );
};

AddAccountFormContent.displayName = 'AddAccountFormContent';
